import React, { useState, useEffect, useRef } from 'react'
import { ChevronLeft, ChevronRight, X, Play, Pause } from 'lucide-react'
import { Section } from '../../types/Section'

interface PhotosSectionProps {
  section: Section
}

interface Photo {
  id: string
  url: string
  caption?: string
  thumbnailUrl?: string
}

export const PhotosSection: React.FC<PhotosSectionProps> = ({ section }) => {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  // Convert attachments to photos array
  const photos: Photo[] = section.attachments?.map((attachment, index) => ({
    id: attachment.id || `photo-${index}`,
    url: attachment.url || '',
    caption: attachment.caption,
    thumbnailUrl: attachment.thumbnailUrl || attachment.url || ''
  })) || []

  const displayStyle = section.options?.display_style || 'gallery'

  // Auto-play functionality for carousel
  useEffect(() => {
    if (isPlaying && displayStyle === 'carousel' && photos.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % photos.length)
      }, 4000) // Change slide every 4 seconds

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
        }
      }
    }
  }, [isPlaying, photos.length, displayStyle])

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying)
  }

  const openLightbox = (index: number) => {
    setSelectedPhotoIndex(index)
    setIsPlaying(false) // Stop autoplay when lightbox opens
  }

  const closeLightbox = () => {
    setSelectedPhotoIndex(null)
  }

  const navigateLightbox = (direction: 'prev' | 'next') => {
    if (selectedPhotoIndex === null) return
    
    const newIndex = direction === 'prev' 
      ? (selectedPhotoIndex - 1 + photos.length) % photos.length
      : (selectedPhotoIndex + 1) % photos.length
    
    setSelectedPhotoIndex(newIndex)
  }

  const navigateCarousel = (direction: 'prev' | 'next') => {
    setIsPlaying(false) // Stop autoplay when user navigates
    const newSlide = direction === 'prev'
      ? (currentSlide - 1 + photos.length) % photos.length
      : (currentSlide + 1) % photos.length
    
    setCurrentSlide(newSlide)
  }

  // Grid Layout
  const renderGrid = () => {
    const columns = section.options?.columns || 3
    const gridClass = `grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-${columns}`

    return (
      <div className={gridClass}>
        {photos.map((photo, index) => (
          <div
            key={photo.id}
            className="relative group cursor-pointer overflow-hidden rounded-lg aspect-square bg-gray-100"
            onClick={() => openLightbox(index)}
          >
            <img
              src={photo.thumbnailUrl || photo.url}
              alt={photo.caption || `Photo ${index + 1}`}
              className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-110"
            />
            {photo.caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <p className="text-white text-sm">{photo.caption}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    )
  }

  // Carousel Layout
  const renderCarousel = () => {
    if (photos.length === 0) return null

    return (
      <div className="relative">
        <div className="overflow-hidden rounded-lg bg-gray-100">
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {photos.map((photo, index) => (
              <div
                key={photo.id}
                className="w-full flex-shrink-0 cursor-pointer"
                onClick={() => openLightbox(index)}
              >
                <div className="relative">
                  <img
                    src={photo.url}
                    alt={photo.caption || `Photo ${index + 1}`}
                    className="w-full h-[400px] md:h-[500px] lg:h-[600px] object-contain"
                  />
                  {photo.caption && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                      <p className="text-white text-lg">{photo.caption}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Carousel Navigation */}
        {photos.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation()
                navigateCarousel('prev')
              }}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-colors"
              aria-label="Previous photo"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                navigateCarousel('next')
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-colors"
              aria-label="Next photo"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Play/Pause button */}
            <button
              onClick={(e) => {
                e.stopPropagation()
                togglePlayPause()
              }}
              className="absolute bottom-4 right-4 bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-colors"
              aria-label={isPlaying ? "Pause slideshow" : "Play slideshow"}
            >
              {isPlaying ? (
                <Pause className="w-5 h-5" />
              ) : (
                <Play className="w-5 h-5" />
              )}
            </button>

            {/* Carousel Indicators */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {photos.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation()
                    setCurrentSlide(index)
                    setIsPlaying(false) // Stop autoplay when user clicks indicator
                  }}
                  className={`w-2 h-2 rounded-full transition-all ${
                    currentSlide === index 
                      ? 'bg-white w-8' 
                      : 'bg-white/50 hover:bg-white/70'
                  }`}
                  aria-label={`Go to photo ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    )
  }

  // Gallery Layout (Horizontal Scroll)
  const renderGallery = () => {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {section.title && (
          <h2 className="text-2xl font-bold mb-6">{section.title}</h2>
        )}
        <div className="overflow-x-auto">
          <div className="flex gap-4 pb-2">
            {photos.map((photo, index) => (
              <div
                key={photo.id}
                className="flex-shrink-0 w-80 h-60 relative group cursor-pointer bg-gray-50 rounded-lg overflow-hidden"
                onClick={() => openLightbox(index)}
              >
                <img
                  src={photo.url}
                  alt={photo.caption || `Photo ${index + 1}`}
                  className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                />
                {photo.caption && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="text-white text-sm">{photo.caption}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // Lightbox Component
  const renderLightbox = () => {
    if (selectedPhotoIndex === null || !photos[selectedPhotoIndex]) return null

    const selectedPhoto = photos[selectedPhotoIndex]

    return (
      <div
        className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
        onClick={closeLightbox}
      >
        <button
          onClick={closeLightbox}
          className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
          aria-label="Close lightbox"
        >
          <X className="w-8 h-8" />
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation()
            navigateLightbox('prev')
          }}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
          aria-label="Previous photo"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation()
            navigateLightbox('next')
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
          aria-label="Next photo"
        >
          <ChevronRight className="w-8 h-8" />
        </button>

        <div
          className="max-w-7xl max-h-[90vh] mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={selectedPhoto.url}
            alt={selectedPhoto.caption || `Photo ${selectedPhotoIndex + 1}`}
            className="max-w-full max-h-[80vh] object-contain"
          />
          {selectedPhoto.caption && (
            <p className="text-white text-center mt-4">{selectedPhoto.caption}</p>
          )}
          <p className="text-white/60 text-center mt-2">
            {selectedPhotoIndex + 1} / {photos.length}
          </p>
        </div>
      </div>
    )
  }

  if (photos.length === 0) {
    return null
  }

  return (
    <div className="photos-section">
      {displayStyle !== 'gallery' && section.title && (
        <h2 className="text-2xl font-bold mb-6">{section.title}</h2>
      )}
      
      {displayStyle === 'gallery' && renderGallery()}
      {displayStyle === 'grid' && renderGrid()}
      {displayStyle === 'carousel' && renderCarousel()}
      
      {renderLightbox()}
    </div>
  )
}